import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { Area, AreaChart, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { ArrowRight, CheckCircle2, MapPin, Clock, Loader2 } from "lucide-react";
import { euro } from "@/data/partnerConfig";
import { api } from "@/lib/api";
import { usePartner, APPOINTMENT_TYPE_LABELS, buildRealDocs, ONBOARDING_STAGE_LABELS } from "../PartnerContext";
import { Card, Delta, SectionTitle, Btn, StatusBadge, Pill } from "../ui";
import PartnerRoleDashboard from "./PartnerRoleDashboard";

const RealStatCard = ({ title, value, testid }) => (
  <Card testid={testid} className="p-5">
    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">{title}</p>
    <p className="mt-2 font-display text-3xl font-medium leading-none text-navy sm:text-4xl">{value}</p>
  </Card>
);

const RealMoeRevenueBlock = () => {
  const [finance, setFinance] = useState(null);
  const [commissions, setCommissions] = useState(null);
  useEffect(() => {
    api.get("/phase3/partner/finance/global").then((r) => setFinance(r.data)).catch(() => {});
    api.get("/phase3/partner/commissions/summary").then((r) => setCommissions(r.data)).catch(() => {});
  }, []);
  if (!finance || !commissions) return null;
  const caPartenaire = finance.partner_fees || 0;
  const commissionsPercues = commissions.total_partner_amount || 0;
  const revenusActivite = caPartenaire + commissionsPercues;
  return (
    <Card testid="real-moe-revenue" className="mt-6 overflow-hidden">
      <div className="h-1 bg-petrol" />
      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-medium text-navy sm:text-2xl">Maîtrise d'œuvre</h2>
          <Pill tone="petrol">Revenus réels</Pill>
        </div>
        <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl bg-petrol-bg p-3">
            <dt className="text-[11px] font-bold text-stone-500">CA partenaire (honoraires MOE)</dt>
            <dd className="mt-1 font-display text-2xl font-medium text-navy" data-testid="moe-ca-partenaire">{euro(caPartenaire)}</dd>
          </div>
          <div className="rounded-xl bg-petrol-bg p-3">
            <dt className="text-[11px] font-bold text-stone-500">Commissions perçues</dt>
            <dd className="mt-1 font-display text-2xl font-medium text-navy" data-testid="moe-commissions-percues">{euro(commissionsPercues)}</dd>
          </div>
          <div className="rounded-xl bg-petrol-tint p-3">
            <dt className="text-[11px] font-bold text-petrol">Revenus activité</dt>
            <dd className="mt-1 font-display text-2xl font-medium text-navy" data-testid="moe-revenus-activite">{euro(revenusActivite)}</dd>
          </div>
        </dl>
        <p className="mt-3 text-[11px] text-stone-400">Commissions perçues = part partenaire des commissions entreprises réellement encaissées par la Centrale (moteur de commissions). CA partenaire = honoraires de maîtrise d'œuvre, calcul distinct.</p>
      </div>
    </Card>
  );
};

const MONTH_LABELS_FR = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const lastMonths = (n) => {
  const out = [];
  const base = new Date();
  base.setDate(1);
  for (let i = n - 1; i >= 0; i--) {
    const dt = new Date(base.getFullYear(), base.getMonth() - i, 1);
    out.push({ key: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`, label: MONTH_LABELS_FR[dt.getMonth()] });
  }
  return out;
};

// Graphiques d'évolution restaurés — mêmes composants recharts que le tableau de bord historique, alimentés en données réelles
const RealActivityCharts = ({ territorial }) => {
  const [monthlyFinance, setMonthlyFinance] = useState(null);
  useEffect(() => { api.get("/phase3/partner/finance/monthly").then((r) => setMonthlyFinance(r.data.by_month || [])).catch(() => setMonthlyFinance([])); }, []);
  if (monthlyFinance === null) return null;

  const months = lastMonths(6);
  const prospectsByMonth = {};
  const signaturesByMonth = {};
  (territorial.prospects || []).forEach((p) => {
    const created = (p.created_at || "").slice(0, 7);
    if (created) prospectsByMonth[created] = (prospectsByMonth[created] || 0) + 1;
    if (["ACCEPTED", "NEIGHBOR_ACCEPTED"].includes(p.routing_status) && p.accepted_at) {
      const acc = p.accepted_at.slice(0, 7);
      signaturesByMonth[acc] = (signaturesByMonth[acc] || 0) + 1;
    }
  });
  const financeByMonth = {};
  monthlyFinance.forEach((e) => { financeByMonth[e.month] = e; });

  const activityData = months.map(({ key, label }) => ({ m: label, prospects: prospectsByMonth[key] || 0, signatures: signaturesByMonth[key] || 0 }));
  const revenueData = months.map(({ key, label }) => ({
    m: label,
    revenue: Math.round((financeByMonth[key]?.revenue || 0) / 100) / 10,
    commissions: Math.round((financeByMonth[key]?.commissions || 0) / 100) / 10,
  }));

  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      <Card testid="real-revenue-chart" className="flex flex-col p-5 sm:p-6">
        <SectionTitle
          right={
            <div className="flex gap-3 text-[11px] font-bold text-stone-500">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-petrol" /> Honoraires MOE</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-cognac" /> Commissions</span>
            </div>
          }
        >
          Évolution du chiffre d'affaires & des commissions
        </SectionTitle>
        <div className="min-h-[176px] min-w-0 flex-1">
          <ResponsiveContainer width="100%" height={176} minWidth={0}>
            <AreaChart data={revenueData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
              <defs>
                <linearGradient id="realCaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#284E55" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#284E55" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#EEF1F2" />
              <XAxis dataKey="m" tick={{ fontSize: 11, fill: "#8A8F94" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#8A8F94" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}k`} />
              <Tooltip formatter={(v, n) => [`${v} k€`, n === "revenue" ? "Honoraires MOE" : "Commissions"]} contentStyle={{ borderRadius: 12, border: "1px solid #E3E8E9", fontSize: 12 }} />
              <Area type="monotone" dataKey="revenue" stroke="#284E55" strokeWidth={2.5} fill="url(#realCaFill)" isAnimationActive={false} />
              <Area type="monotone" dataKey="commissions" stroke="#B57A51" strokeWidth={2.5} fill="transparent" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-2 text-[11px] text-stone-400">Honoraires MOE encaissés et commissions perçues (k€), par mois — moteur de commissions réel, aucune donnée simulée.</p>
      </Card>
      <Card testid="real-prospects-chart" className="flex flex-col p-5 sm:p-6">
        <SectionTitle
          right={
            <div className="flex gap-3 text-[11px] font-bold text-stone-500">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-petrol" /> Prospects</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-cognac" /> Signatures</span>
            </div>
          }
        >
          Évolution des prospects & signatures
        </SectionTitle>
        <div className="min-h-[176px] min-w-0 flex-1">
          <ResponsiveContainer width="100%" height={176} minWidth={0}>
            <LineChart data={activityData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#EEF1F2" />
              <XAxis dataKey="m" tick={{ fontSize: 11, fill: "#8A8F94" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#8A8F94" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E3E8E9", fontSize: 12 }} />
              <Line type="monotone" dataKey="prospects" stroke="#284E55" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
              <Line type="monotone" dataKey="signatures" stroke="#B57A51" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-2 text-[11px] text-stone-400">Prospects reçus et dossiers acceptés (signatures), par mois — données réelles de votre pipeline territorial.</p>
      </Card>
    </div>
  );
};

const fmtApptDate = (iso) => {
  const d = dayjs(iso);
  const today = dayjs();
  if (d.isSame(today, "day")) return `Aujourd'hui ${d.format("HH:mm")}`;
  if (d.isSame(today.add(1, "day"), "day")) return `Demain ${d.format("HH:mm")}`;
  return d.format("DD/MM HH:mm");
};

const UpcomingAppointmentsCard = ({ appointments }) => (
  <Card testid="dashboard-upcoming-appointments" className="mt-6 p-5 sm:p-6">
    <SectionTitle right={<Link to="/partenaire/agenda" data-testid="dashboard-appointments-link" className="text-xs font-bold text-petrol hover:underline">Voir l'agenda</Link>}>
      Prochains rendez-vous
    </SectionTitle>
    {!appointments || appointments.length === 0 ? (
      <p className="text-sm text-stone-500">Aucun rendez-vous planifié.</p>
    ) : (
      <ul className="space-y-2">
        {appointments.map((a) => (
          <li key={a.id}>
            <Link
              to={`/partenaire/agenda?date=${a.start_at.slice(0, 10)}`}
              data-testid={`dashboard-appointment-${a.id}`}
              className="flex items-center justify-between gap-3 rounded-xl bg-petrol-bg px-3.5 py-2.5 text-sm transition-colors hover:bg-petrol-tint"
            >
              <span className="font-semibold text-navy">{fmtApptDate(a.start_at)} — {a.prospect_name}</span>
              <span className="text-xs text-stone-500">{APPOINTMENT_TYPE_LABELS[a.appointment_type]}</span>
            </Link>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

const RealOwnerDashboard = () => {
  const { currentUser, territorial, territoryPendingActivation } = usePartner();
  const stats = territorial.stats;
  return (
    <div data-testid="partner-dashboard-real">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl" data-testid="dashboard-greeting">
          Bonjour {currentUser.firstName}
        </h1>
        <p className="mt-1.5 text-sm text-stone-500">Voici l'activité réelle de votre secteur CozyHome.</p>
      </div>

      {territoryPendingActivation && (
        <div data-testid="dashboard-territory-pending-banner" className="mb-5 flex items-start gap-3 rounded-2xl bg-petrol-bg p-4 text-sm leading-relaxed text-petrol">
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          Votre territoire est en attente d'activation par CozyHome Central. Votre espace est déjà accessible, mais aucun projet ne vous sera transmis avant l'activation.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <RealStatCard testid="kpi-real-nouveaux" title="Nouveaux prospects" value={stats.nouveaux} />
        <RealStatCard testid="kpi-real-acceptes" title="Acceptés" value={stats.acceptes} />
        <RealStatCard testid="kpi-real-refuses" title="Refusés" value={stats.refuses} />
        <RealStatCard testid="kpi-real-total" title="Total prospects" value={stats.total} />
      </div>

      <RealMoeRevenueBlock />
      <UpcomingAppointmentsCard appointments={territorial.upcoming_appointments} />

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card testid="dashboard-real-territories" className="p-5 sm:p-6">
          <SectionTitle>Mes territoires</SectionTitle>
          {territorial.territories.length === 0 ? (
            <p className="text-sm text-stone-500">Aucun territoire attribué pour le moment.</p>
          ) : (
            <ul className="space-y-2">
              {territorial.territories.map((t) => (
                <li key={t.id} className="flex items-center gap-3 rounded-xl bg-petrol-bg p-3">
                  <MapPin className="h-4 w-4 shrink-0 text-petrol" />
                  <div>
                    <p className="text-sm font-semibold text-navy">{t.name}</p>
                    <p className="text-xs text-stone-500">{t.department} · {t.commune_count} communes · {t.status === "attribue" ? "Actif" : t.status}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card testid="dashboard-real-prospects-link" className="flex flex-col justify-between p-5 sm:p-6">
          <div>
            <SectionTitle>Prospects à traiter</SectionTitle>
            <p className="text-sm text-stone-500">{stats.nouveaux} nouveau{stats.nouveaux > 1 ? "x" : ""} projet{stats.nouveaux > 1 ? "s" : ""} en attente de votre réponse.</p>
          </div>
          <Link to="/partenaire/prospects" data-testid="dashboard-real-prospects-cta" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-petrol hover:underline">
            Voir mes prospects <ArrowRight className="h-4 w-4" />
          </Link>
        </Card>
      </div>

      <RealActivityCharts territorial={territorial} />
    </div>
  );
};

const OnboardingDashboard = () => {
  const { partner, territorial } = usePartner();
  const docs = territorial ? buildRealDocs(territorial) : [];
  const done = docs.filter((d) => d.status === "Validé").length;
  const pct = docs.length ? Math.round((done / docs.length) * 100) : 0;
  const stageLabel = territorial ? (ONBOARDING_STAGE_LABELS[territorial.partner.activation_status] || "Dossier en cours de traitement") : "Dossier en cours de traitement";
  return (
    <div data-testid="partner-onboarding-dashboard">
      <Card className="overflow-hidden">
        <div className="h-1.5 bg-cognac" />
        <div className="p-6 sm:p-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-petrol">Bienvenue chez CozyHome</p>
          <h1 className="mt-2 max-w-2xl font-display text-3xl font-medium leading-tight text-navy sm:text-4xl">
            Bonjour {partner.firstName}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone-600 sm:text-base" data-testid="onboarding-stage-label">
            {stageLabel}. Complétez votre dossier professionnel afin de finaliser votre intégration au réseau.
            L'accès complet à l'espace Partenaire et l'attribution de votre secteur interviendront après
            vérification de votre dossier par CozyHome.
          </p>
          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
            <Btn to="/partenaire/entreprise" testid="onboarding-cta-dossier">
              Compléter mon dossier <ArrowRight className="h-4 w-4" />
            </Btn>
            {docs.length > 0 && (
              <div className="flex-1">
                <div className="flex items-center justify-between text-xs font-bold text-stone-500">
                  <span>Avancement du dossier</span>
                  <span data-testid="onboarding-progress">{done}/{docs.length} éléments validés</span>
                </div>
                <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-petrol-tint">
                  <div className="h-full rounded-full bg-petrol transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="mt-6 grid gap-5 lg:grid-cols-5">
        <Card className="p-6 lg:col-span-3" testid="onboarding-doc-list">
          <SectionTitle>Votre dossier professionnel</SectionTitle>
          {docs.length === 0 ? (
            <p className="text-sm text-stone-500">Aucun document requis pour le moment.</p>
          ) : (
            <ul className="divide-y divide-[#F0F2F3]">
              {docs.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <span className="font-semibold text-navy">{d.name}</span>
                  <StatusBadge status={d.status} />
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="p-6 lg:col-span-2">
          <SectionTitle>Les prochaines étapes</SectionTitle>
          <ol className="space-y-3 text-sm text-stone-600">
            {["Transmettre les documents manquants", "Vérification par CozyHome", "Validation du dossier", "Activation de votre secteur", "Accès complet à l'espace Partenaire"].map((s, i) => (
              <li key={s} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-petrol-tint font-display text-sm font-semibold text-petrol">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
          <p className="mt-5 rounded-xl bg-petrol-bg p-3 text-xs leading-relaxed text-stone-500">
            Les fonctions opérationnelles (prospects, projets, services, communication) s'ouvriront après validation.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default function PartnerDashboard() {
  const { validated, internalRole, backend, territorial, territorialLoading } = usePartner();
  if (backend && territorialLoading) {
    return (
      <div data-testid="partner-dashboard-loading" className="flex min-h-[50vh] items-center justify-center text-stone-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (!validated) return <OnboardingDashboard />;
  if (internalRole !== "OWNER") return <PartnerRoleDashboard />;
  if (backend && territorial) return <RealOwnerDashboard />;
  return (
    <div data-testid="partner-dashboard-loading" className="flex min-h-[50vh] items-center justify-center text-stone-400">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
}
